import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Warning, Trash, ShieldWarning } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONFIRM_PHRASE = 'CONFIRM-WIPE-EMPLOYEE-DATA';

export default function DangerZoneSection() {
  const { language } = useLanguage();
  const T = (ar, en) => (language === 'ar' ? ar : en);

  const [categories, setCategories] = useState([]);
  const [preserved, setPreserved] = useState([]);
  const [selectedMap, setSelectedMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [wiping, setWiping] = useState(false);

  const selectedKeys = Object.keys(selectedMap).filter(k => selectedMap[k]);
  const selectedCount = selectedKeys.length;

  const fetchStats = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/system/data-stats`, { withCredentials: true });
      setCategories(data.categories || []);
      setPreserved(data.preserved || []);
    } catch (e) {
      console.error(e);
      toast.error(T('تعذر جلب الإحصائيات', 'Failed to load stats'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/admin/system/data-stats`, { withCredentials: true });
        if (!active) return;
        setCategories(data.categories || []);
        setPreserved(data.preserved || []);
      } catch (e) {
        console.error(e);
        if (active) toast.error(T('تعذر جلب الإحصائيات', 'Failed to load stats'));
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggle = (key) => {
    setSelectedMap({ ...selectedMap, [key]: !selectedMap[key] });
  };

  const toggleAll = () => {
    if (selectedCount === categories.length) {
      setSelectedMap({});
    } else {
      const next = {};
      categories.forEach(c => { next[c.key] = true; });
      setSelectedMap(next);
    }
  };

  const totalSelected = categories
    .filter(c => selectedMap[c.key])
    .reduce((s, c) => s + c.count, 0);

  const handleWipe = async () => {
    if (confirmText !== CONFIRM_PHRASE) {
      toast.error(T('عبارة التأكيد غير صحيحة', 'Confirmation phrase mismatch'));
      return;
    }
    if (selectedCount === 0) {
      toast.error(T('اختر فئة واحدة على الأقل', 'Select at least one category'));
      return;
    }
    setWiping(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/system/wipe-employee-data`,
        { confirmation: CONFIRM_PHRASE, categories: selectedKeys },
        { withCredentials: true }
      );
      const totalDeleted =
        (data.deleted_users || 0) +
        Object.values(data.cleared_collections || {}).reduce((s, v) => s + v, 0);
      toast.success(
        T(`تم حذف ${totalDeleted} سجلاً بنجاح`, `Deleted ${totalDeleted} records successfully`)
      );
      setShowConfirm(false);
      setConfirmText('');
      setSelectedMap({});
      await fetchStats();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || T('فشل الحذف', 'Wipe failed'));
    } finally {
      setWiping(false);
    }
  };

  if (loading) return null;

  return (
    <Card
      className="border-red-200 bg-gradient-to-br from-red-50/40 to-rose-50/30"
      data-testid="danger-zone-card"
    >
      <CardHeader>
        <CardTitle className="text-red-700 flex items-center gap-2">
          <ShieldWarning size={22} weight="duotone" />
          {T('منطقة الخطر — حذف بيانات النظام', 'Danger Zone — Delete System Data')}
        </CardTitle>
        <p className="text-xs text-red-600/80 mt-1">
          {T(
            '⚠️ عملية تدميرية لا يمكن التراجع عنها. اختر الفئات المراد حذفها بدقّة.',
            '⚠️ Destructive, irreversible operation. Choose categories carefully.'
          )}
        </p>
      </CardHeader>

      <CardContent className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Badge
            variant="outline"
            className="border-red-300 text-red-700 bg-white"
            data-testid="danger-selected-badge"
          >
            {T(
              `محدد: ${selectedCount} / ${categories.length} — مجموع السجلات: ${totalSelected}`,
              `Selected: ${selectedCount} / ${categories.length} — Total records: ${totalSelected}`
            )}
          </Badge>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={toggleAll}
            className="border-red-300 text-red-700 hover:bg-red-50"
            data-testid="danger-select-all-btn"
          >
            {selectedCount === categories.length
              ? T('إلغاء تحديد الكل', 'Deselect all')
              : T('تحديد الكل', 'Select all')}
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {categories.map(cat => {
            const isOn = !!selectedMap[cat.key];
            const empty = cat.count === 0;
            return (
              <label
                key={cat.key}
                className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                  isOn
                    ? 'border-red-400 bg-red-50/70 shadow-sm'
                    : 'border-red-100 bg-white hover:bg-red-50/30'
                } ${empty ? 'opacity-60' : ''}`}
                data-testid={`danger-cat-${cat.key}`}
              >
                <Checkbox
                  checked={isOn}
                  onCheckedChange={() => toggle(cat.key)}
                  disabled={empty}
                  className="mt-0.5 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                  data-testid={`danger-cat-checkbox-${cat.key}`}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium text-gray-800">
                      {language === 'ar' ? cat.label_ar : cat.label_en}
                    </span>
                    <Badge
                      className={`text-[10px] ${
                        cat.count > 0
                          ? 'bg-red-100 text-red-700 border-red-200'
                          : 'bg-gray-100 text-gray-500 border-gray-200'
                      }`}
                    >
                      {cat.count} {T('سجل', 'records')}
                    </Badge>
                  </div>
                  <p className="text-[10px] text-gray-500 mt-1 truncate">
                    {(cat.collections || []).join(' · ')}
                  </p>
                </div>
              </label>
            );
          })}
        </div>

        <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-[11px] text-emerald-800">
          <span className="font-semibold">{T('محميّ دائماً:', 'Always preserved:')} </span>
          {preserved.join(' · ')}
        </div>

        <Button
          type="button"
          onClick={() => setShowConfirm(true)}
          disabled={selectedCount === 0}
          className="w-full bg-red-600 hover:bg-red-700 text-white gap-2 disabled:opacity-50"
          data-testid="danger-open-confirm-btn"
        >
          <Trash size={18} weight="bold" />
          {T('متابعة الحذف…', 'Proceed to delete…')}
        </Button>
      </CardContent>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirm} onOpenChange={(o) => { if (!wiping) setShowConfirm(o); }}>
        <DialogContent className="max-w-md" data-testid="danger-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2">
              <Warning size={22} weight="fill" />
              {T('تأكيد الحذف النهائي', 'Final Confirmation')}
            </DialogTitle>
            <DialogDescription className="text-red-600/80">
              {T(
                `سيتم حذف ${totalSelected} سجلاً عبر ${selectedCount} فئة. هذا الإجراء لا يمكن التراجع عنه.`,
                `You are about to delete ${totalSelected} records across ${selectedCount} categories. This cannot be undone.`
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="rounded-md bg-red-50 border border-red-200 p-3 text-[12px] text-red-800 space-y-1">
              <p className="font-semibold">{T('الفئات المختارة:', 'Selected categories:')}</p>
              <ul className="list-disc list-inside text-[11px]">
                {categories.filter(c => selectedMap[c.key]).map(c => (
                  <li key={c.key}>
                    {language === 'ar' ? c.label_ar : c.label_en}
                    <span className="text-red-600/70 ms-2">({c.count} {T('سجل', 'records')})</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs text-gray-700">
                {T(
                  <>اكتب <code className="bg-gray-100 px-1.5 py-0.5 rounded text-red-700">{CONFIRM_PHRASE}</code> للتأكيد</>,
                  <>Type <code className="bg-gray-100 px-1.5 py-0.5 rounded text-red-700">{CONFIRM_PHRASE}</code> to confirm</>
                )}
              </Label>
              <Input
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={CONFIRM_PHRASE}
                className="font-mono text-sm border-red-300 focus:ring-red-500"
                dir="ltr"
                disabled={wiping}
                data-testid="danger-confirm-input"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => { setShowConfirm(false); setConfirmText(''); }}
              disabled={wiping}
              data-testid="danger-cancel-btn"
            >
              {T('إلغاء', 'Cancel')}
            </Button>
            <Button
              onClick={handleWipe}
              disabled={confirmText !== CONFIRM_PHRASE || wiping}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="danger-execute-btn"
            >
              {wiping
                ? T('جارٍ الحذف…', 'Deleting…')
                : T('نعم، احذف نهائياً', 'Yes, delete permanently')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
